import React, { useEffect, useRef, useState } from "react"; // Importar useState
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDModal from "components/MDModal";

function CreditSummaryModal({ open, onClose, selectedQuantity, totalPrice, onIncreaseCredits, onDecreaseCredits }) {
  const epaycoButtonContainer = useRef(null);
  const [epaycoLoaded, setEpaycoLoaded] = useState(false);
  
  const formatCurrency = (value) => {
    return value.toLocaleString('es-CO', { style: 'currency', currency: 'COP' });
  };

  // Cargar el script de ePayco una sola vez al montar el componente
  useEffect(() => {
    const scriptId = 'epayco-checkout-script';
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://checkout.epayco.co/checkout.js';
      script.id = scriptId;
      script.async = true;
      script.onload = () => setEpaycoLoaded(true);
      script.onerror = () => console.error('ePayco script failed to load.');
      document.body.appendChild(script);
    } else if (window.ePayco) { // Si el script ya existe y window.ePayco está disponible
      setEpaycoLoaded(true);
    }

    return () => {
      // No removemos el script aquí; se carga una sola vez para toda la aplicación.
    };
  }, []); // Dependencia vacía para que se ejecute solo una vez al montar

  // Crear/actualizar el botón de ePayco cuando el modal está abierto, el script cargado y los datos cambian
  useEffect(() => {
    if (open && epaycoLoaded && epaycoButtonContainer.current && totalPrice > 0) {
      // Limpiar cualquier botón renderizado previamente
      epaycoButtonContainer.current.innerHTML = "";
      
      const newButtonDiv = document.createElement('div');
      newButtonDiv.className = 'epayco-button';
      newButtonDiv.setAttribute('data-epayco-key', 'a144bdba8b691ebb3be439308573767a');
      newButtonDiv.setAttribute('data-epayco-amount', totalPrice.toString());
      newButtonDiv.setAttribute('data-epayco-tax', '0.00');
      newButtonDiv.setAttribute('data-epayco-tax-ico', '0.00');
      newButtonDiv.setAttribute('data-epayco-tax-base', totalPrice.toString());
      newButtonDiv.setAttribute('data-epayco-name', 'Compra de Créditos Quadrante H');
      newButtonDiv.setAttribute('data-epayco-description', `Compra de ${selectedQuantity} créditos para el Test Quadrante H`);
      newButtonDiv.setAttribute('data-epayco-currency', 'cop');
      newButtonDiv.setAttribute('data-epayco-country', 'CO');
      newButtonDiv.setAttribute('data-epayco-test', 'false');
      newButtonDiv.setAttribute('data-epayco-external', 'false');
      newButtonDiv.setAttribute('data-epayco-response', '');
      newButtonDiv.setAttribute('data-epayco-confirmation', '');
      newButtonDiv.setAttribute('data-epayco-button', 'https://multimedia.epayco.co/dashboard/btns/btn5.png');
      epaycoButtonContainer.current.appendChild(newButtonDiv);

    } else if (!open && epaycoButtonContainer.current) { // Limpiar cuando el modal se cierra
      epaycoButtonContainer.current.innerHTML = "";
    }
  }, [open, epaycoLoaded, totalPrice, selectedQuantity]); // Dependencias: open, epaycoLoaded, totalPrice, selectedQuantity

  return (
    <MDModal open={open} onClose={onClose} title="Créditos Quadrante H">
      <Card
        sx={({ palette: { info, primary, white }, functions: { rgba, linearGradient } }) => ({
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "500px",
          marginLeft: "auto", // Added to center the card horizontally
          marginRight: "auto", // Added to center the card horizontally
          p: 4,
          backgroundColor: rgba(white.main, 0.8), // Volver al blanco transparente
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          overflow: "hidden",
          "&:hover": {
            boxShadow: ({ boxShadows: { primary } }) => primary, // Nuevo efecto de sombra en hover
          },
        })}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
          {/* <MDTypography variant="h6" component="h2">
            Resumen de la Compra
          </MDTypography> */}
          {/* <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton> */}
        </MDBox>
        <Divider sx={{ width: "100%", mb: 2 }} />
        <MDBox mt={2} mb={2} width="100%">
          <MDBox display="flex" alignItems="center" justifyContent="center" mb={1}>
            <MDTypography variant="body2" color="text">
              Créditos seleccionados:
            </MDTypography>
            <MDBox display="flex" alignItems="center" ml={1}> {/* Añadido ml para espacio */}
              <MDBox // Circulo para el boton de disminuir
                sx={({ palette: { grey, dark, white }, borders: { borderRadius, borderWidth }, functions: { rgba } }) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  border: `${borderWidth[1]} solid ${rgba(dark.main, 0.7)}`,
                  backgroundColor: dark.main, // Fondo negro (dark.main)
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: grey[900], // Hover aún más oscuro
                  },
                })}
              >
                <IconButton size="small" onClick={onDecreaseCredits} disabled={selectedQuantity <= 1} sx={{ p: 0, color: '#FFFFFF' }}> 
                  <Icon>remove</Icon>
                </IconButton>
              </MDBox>
              <MDTypography variant="body2" fontWeight="bold" color="text" mx={1}>
                {selectedQuantity}
              </MDTypography>
              <MDBox // Circulo para el boton de aumentar
                sx={({ palette: { grey, success, white }, borders: { borderRadius, borderWidth }, functions: { rgba } }) => ({
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  border: `${borderWidth[1]} solid ${rgba(success.main, 0.7)}`,
                  backgroundColor: success.main, // Fondo verde
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: success.light, // Hover más claro
                  },
                })}
              >
                <IconButton size="small" onClick={onIncreaseCredits} disabled={selectedQuantity >= 50} sx={{ p: 0, color: '#FFFFFF' }}> 
                  <Icon>add</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider sx={{ width: "100%", mt: 2 }} />
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mt={2} mb={3} width="100%">
          <MDTypography variant="h6" color="text">
            Total a Pagar:
          </MDTypography>
          <MDTypography variant="h6" color="text">
            {formatCurrency(totalPrice)}
          </MDTypography>
        </MDBox>
        <MDBox mt={3} mb={1} width="100%" display="flex" justifyContent="center">
          <form>
            <div
                ref={epaycoButtonContainer}
                className='epayco-button'
                // Los atributos data-epayco-* se configurarán dinámicamente en el useEffect
                // para asegurar que siempre tengan los valores correctos.
            >
            </div>
          </form>
        </MDBox>
      </Card>
    </MDModal>
  );
}

CreditSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedQuantity: PropTypes.number.isRequired,
  totalPrice: PropTypes.number.isRequired,
  onIncreaseCredits: PropTypes.func.isRequired,
  onDecreaseCredits: PropTypes.func.isRequired,
};

export default CreditSummaryModal;